import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { z } from 'zod';
import { FaBox, FaCreditCard, FaAngleDown, FaBoxOpen, FaTruck, FaTimes, FaSave } from 'react-icons/fa';
import { FaMapMarkerAlt, FaFileInvoice } from 'react-icons/fa';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { IoIosCloseCircle } from "react-icons/io";
import { zodResolver } from '@hookform/resolvers/zod';
import { useMyContext } from '../../../context/MyContext';
import { FaWarehouse } from "react-icons/fa6";
import '../../css/custom-scrollbar.css';
import { Base_Url,   updateB2b,b2bWarehouseListAll } from '../../../config/config';
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';

const schema = z.object({
    // Basic Shipment Information
    order_id: z.string()
        .min(1, "Order ID is required")
        .regex(/^[A-Za-z0-9-_]+$/, "Order ID must contain only alphanumeric characters, hyphens, and underscores"),

    // lrn: z.string()
    //     .min(1, "LRN is required")
    //     .regex(/^[A-Za-z0-9-_]+$/, "LRN must contain only alphanumeric characters, hyphens, and underscores"),

    lrn: z.string().optional(),
    total_boxes: z.number()
        .min(1, "Total boxes must be at least 1")
        .int("Total boxes must be a whole number"),

    total_weight: z.number()
        .min(0.1, "Total weight must be greater than 0")
        .max(1000000, "Total weight cannot exceed 1,000,000 kg"),

    rov_insurance: z.number()
        .min(0, "ROV Insurance cannot be negative")
        .optional(),

    total_length: z.number()
        .min(0.1, "Total length must be greater than 0")
        .max(10000, "Total length cannot exceed 10,000 cm"),

    total_width: z.number()
        .min(0.1, "Total width must be greater than 0")
        .max(10000, "Total width cannot exceed 10,000 cm"),

    total_height: z.number()
        .min(0.1, "Total height must be greater than 0")
        .max(10000, "Total height cannot exceed 10,000 cm"),

    freight_mode: z.string()
        .min(1, "Freight mode is required")
        .regex(/^[A-Za-z\s]+$/, "Freight mode must only contain letters and spaces"),

    shipment_type: z.string().optional(),
    // .min(1, "Shipment type is required")
    // .regex(/^[A-Za-z\s]+$/, "Shipment type must only contain letters and spaces"),


    shipment_order_id: z.string().min(1, "shipment_order_id is required"),
    shipment_weight: z
        .number()
        .positive("Weight must be greater than 0"),
    shipment_description: z.string().min(1, "shipment_description is required"),
    shipment_box_count :  z
        .number()
        .positive("Box count must be greater than 0"),
    // shipment_weight: z
    //     .number()
    //     .positive("Weight must be greater than 0"),

    insurence: z.string().optional(), // Note: keeping the typo as in original form

    // Pickup Location
    pickup_consignee_name: z.string()
        .min(1, "Pickup consignee name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name cannot exceed 100 characters"),
    // .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),

    pickup_phone: z.string()
        .min(10, "Phone number must be at least 10 digits")
        .max(10, "Phone number cannot exceed 10 digits")
        .regex(/^[\+]?[\d\s\-\(\)]+$/, "Phone number format is invalid"),

    pickup_address: z.string()
        .min(10, "Pickup address must be at least 10 characters")
        .max(200, "Address cannot exceed 200 characters"),

    pickup_city: z.string()
        .min(1, "Pickup city is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),

    pickup_state: z.string()
        .min(1, "Pickup state is required")
        .min(2, "State must be at least 2 characters")
        .max(50, "State cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),

    pickup_zip: z.string()
        .min(1, "Pickup ZIP code is required")
        .regex(/^[A-Za-z0-9\s-]+$/, "ZIP code format is invalid")
        .min(3, "ZIP code must be at least 3 characters")
        .max(10, "ZIP code cannot exceed 10 characters"),

    // Dropoff Location
    dropoff_consignee_name: z.string()
        .min(1, "Dropoff consignee name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name cannot exceed 100 characters"),
    // .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),

    dropoff_phone: z.string()
        .min(10, "Phone number must be at least 10 digits")
        .max(15, "Phone number cannot exceed 15 digits")
        .regex(/^[\+]?[\d\s\-\(\)]+$/, "Phone number format is invalid"),

    dropoff_address: z.string()
        .min(10, "Dropoff address must be at least 10 characters")
        .max(200, "Address cannot exceed 200 characters"),

    dropoff_email: z.string()
        .email("Invalid email address")
        .optional()
        .or(z.literal("")),

    dropoff_city: z.string()
        .min(1, "Dropoff city is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),

    dropoff_state: z.string()
        .min(1, "Dropoff state is required")
        .min(2, "State must be at least 2 characters")
        .max(50, "State cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),

    dropoff_zip: z.string()
        .min(1, "Dropoff ZIP code is required")
        .regex(/^[A-Za-z0-9\s-]+$/, "ZIP code format is invalid")
        .min(3, "ZIP code must be at least 3 characters")
        .max(10, "ZIP code cannot exceed 10 characters"),

    // Billing Address
    billing_name: z.string()
        .min(1, "Billing name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name cannot exceed 100 characters")
        .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),

    billing_company: z.string()
        .min(1, "Company name is required")
        .min(2, "Company name must be at least 2 characters")
        .max(100, "Company name cannot exceed 100 characters"),

    billing_consignor: z.string()
        .min(1, "Consignor is required")
        .min(2, "Consignor must be at least 2 characters")
        .max(100, "Consignor cannot exceed 100 characters")
        .regex(/^[a-zA-Z\s.'-]+$/, "Consignor must only contain letters, spaces, periods, apostrophes, and hyphens"),

    billing_phone: z.string()
        .min(10, "Phone number must be at least 10 digits")
        .max(15, "Phone number cannot exceed 15 digits")
        .regex(/^[\+]?[\d\s\-\(\)]/, "Phone number format is invalid"),

    billing_address: z.string()
        .min(10, "Billing address must be at least 10 characters")
        .max(200, "Address cannot exceed 200 characters"),

    billing_city: z.string()
        .min(1, "Billing city is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),

    billing_state: z.string()
        .min(1, "Billing state is required")
        .min(2, "State must be at least 2 characters")
        .max(50, "State cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),

    billing_pin: z.string()
        .min(1, "PIN code is required")
        .regex(/^[A-Za-z0-9\s-]+$/, "PIN code format is invalid")
        .min(3, "PIN code must be at least 3 characters")
        .max(10, "PIN code cannot exceed 10 characters"),

    billing_pan_number: z.string()
        .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "PAN number must be in format: ABCDE1234F")
        .optional()
        .or(z.literal("")),

    billing_gst_number: z.string()
        .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "GST number format is invalid")
        .optional()
        .or(z.literal("")),

    // Payment Details
    payment_id: z.string().optional(),
    // .min(1, "Payment ID is required")
    // .regex(/^[A-Za-z0-9-_]+$/, "Payment ID must contain only alphanumeric characters, hyphens, and underscores"),

    // payment_mode: z.enum([
    //     "COD",
    //     "Prepaid",
    // ], {
    //     errorMap: () => ({ message: "Please select a valid payment mode" })
    // }),
    invoice_num: z.string().optional(),
    payment_mode: z.string().optional(),
    inv_amt: z.string().optional(),

});



const B2BViewAllShipment = ({ isopen, onClose, shipment, fetchOrderData, wareHouses = [] }) => {
    const { isDarkMode } = useMyContext();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    // const [warehouseList, setwareHouseList] = useState([])
    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            order_id: '',
            lrn: '',
            payment_mode: '',
            payment_id: '',
            cod_amount: '',
            total_boxes: '',
            total_weight: '',
            total_length: '',
            total_width: '',
            total_height: '',
            rov_insurance: '',
            freight_mode: '',
            shipment_type: '',
            insurence: '',
            // Pickup location fields
            pickup_consignee_name: '',
            pickup_address: '',
            pickup_city: '',
            pickup_state: '',
            pickup_zip: '',
            pickup_phone: '',
            // Dropoff location fields
            dropoff_consignee_name: '',
            dropoff_address: '',
            dropoff_city: '',
            dropoff_state: '',
            dropoff_zip: '',
            dropoff_phone: '',
            dropoff_email: '',
            // Billing address fields
            billing_name: '',
            billing_company: '',
            billing_consignor: '',
            billing_address: '',
            billing_city: '',
            billing_state: '',
            billing_pin: '',
            billing_phone: '',
            billing_pan_number: '',
            billing_gst_number: '',
            // Invoice fields
            invoice_ewaybill: '',
            invoice_num: '',
            invoice_amt: '',
            invoice_qr_code: '',
            // Shipment details
            shipment_order_id: '',
            shipment_box_count: '',
            shipment_description: '',
            shipment_weight: '',
            shipment_master: '',

        }
    });
    const paymentMode = watch('payment_mode');

    // Check if a field is editable
    const isEditable = (fieldName) => {
        const editableFields = [
            'lrn', 'payment_mode', 'payment_id', 'cod_amount', 'total_boxes',
            'total_weight', 'total_length', 'total_width', 'total_height',
            'rov_insurance', 'freight_mode', 'shipment_type', 'insurence',
            'pickup_consignee_name', 'pickup_address', 'pickup_city', 'pickup_state',
            'pickup_zip', 'pickup_phone', 'dropoff_consignee_name', 'dropoff_address',
            'dropoff_city', 'dropoff_state', 'dropoff_zip', 'dropoff_phone',
            'dropoff_email', 'billing_name', 'billing_company', 'billing_consignor',
            'billing_address', 'billing_city', 'billing_state', 'billing_pin',
            'billing_phone', 'billing_pan_number', 'billing_gst_number',
            'invoice_ewaybill', 'invoice_num', 'invoice_amt', 'invoice_qr_code',
            'shipment_order_id', 'shipment_box_count', 'shipment_description',
            'shipment_weight', 'shipment_master'
        ];
        return editableFields.includes(fieldName) && isEditing; // Only editable when in edit mode
    };

    useEffect(() => {
        if (shipment) {
            try {
                // Parse JSON string fields
                const pickup = shipment.pickup_location ? JSON.parse(shipment.pickup_location) : {};
                const dropoff = shipment.dropoff_location ? JSON.parse(shipment.dropoff_location) : {};
                const billing = shipment.billing_address ? JSON.parse(shipment.billing_address) : {};
                const invoices = shipment.invoices ? JSON.parse(shipment.invoices) : [];
                const shipmentDetails = shipment.shipment_details ? JSON.parse(shipment.shipment_details) : [];

                // Set basic shipment fields
                setValue('order_id', shipment.order_id || '');
                setValue('lrn', shipment.lrn || '');
                setValue('payment_mode', shipment.payment_mode || '');
                setValue('payment_id', shipment.payment_id || '');
                setValue('cod_amount', shipment.cod_amount || '');
                setValue('total_boxes', shipment.total_boxes || '');
                setValue('total_weight', shipment.total_weight || '');
                setValue('total_length', shipment.total_length || '');
                setValue('total_width', shipment.total_width || '');
                setValue('total_height', shipment.total_height || '');
                setValue('rov_insurance', shipment.rov_insurance || '');
                setValue('freight_mode', shipment.freight_mode || '');
                setValue('shipment_type', shipment.shipment_type || '');
                setValue('insurence', shipment.insurence || '');

                // Set pickup location fields
                setValue('pickup_consignee_name', pickup.consignee_name || '');
                setValue('pickup_address', pickup.address || '');
                setValue('pickup_city', pickup.city || '');
                setValue('pickup_state', pickup.state || '');
                setValue('pickup_zip', pickup.zip || '');
                setValue('pickup_phone', pickup.phone || '');

                // Set dropoff location fields
                setValue('dropoff_consignee_name', dropoff.consignee_name || '');
                setValue('dropoff_address', dropoff.address || '');
                setValue('dropoff_city', dropoff.city || '');
                setValue('dropoff_state', dropoff.state || '');
                setValue('dropoff_zip', dropoff.zip || '');
                setValue('dropoff_phone', dropoff.phone || '');
                setValue('dropoff_email', dropoff.email || '');

                // Set billing address fields
                setValue('billing_name', billing.name || '');
                setValue('billing_company', billing.company || '');
                setValue('billing_consignor', billing.consignor || '');
                setValue('billing_address', billing.address || '');
                setValue('billing_city', billing.city || '');
                setValue('billing_state', billing.state || '');
                setValue('billing_pin', billing.pin || '');
                setValue('billing_phone', billing.phone || '');
                setValue('billing_pan_number', billing.pan_number || '');
                setValue('billing_gst_number', billing.gst_number || '');

                // Set invoice fields (first invoice if exists)
                if (invoices.length > 0) {
                    const firstInvoice = invoices[0];
                    setValue('invoice_ewaybill', firstInvoice.ewaybill || '');
                    setValue('invoice_num', firstInvoice.inv_num || '');
                    setValue('invoice_amt', firstInvoice.inv_amt || '');
                    setValue('invoice_qr_code', firstInvoice.inv_qr_code || '');
                }

                // Set shipment details (first shipment if exists)
                if (shipmentDetails.length > 0) {
                    const firstShipment = shipmentDetails[0];
                    setValue('shipment_order_id', firstShipment.order_id || '');
                    setValue('shipment_box_count', firstShipment.box_count || '');
                    setValue('shipment_description', firstShipment.description || '');
                    setValue('shipment_weight', firstShipment.weight || '');
                    setValue('shipment_master', firstShipment.master || '');
                }

            } catch (error) {
                console.error('Error parsing shipment data:', error);
                setError('Error parsing shipment data');
            }
        }
    }, [shipment, setValue]);

    const onErrors = (errors) => {
        toast.error("Please fix the errors in the form.");
    };

    let pickupLocation = {};
    try {
        pickupLocation = shipment?.pickup_location
            ? JSON.parse(shipment.pickup_location)
            : {};
    } catch (error) {
        console.error("Invalid pickup_location JSON", error);
    }

    const WarehouseConsignee = pickupLocation?.consignee_name?.trim();

    const matchedWarehouse = wareHouses.find(
        (wh) =>
            wh?.name?.trim().toLowerCase() ===
            WarehouseConsignee?.toLowerCase()
    );

    const warehouseId = matchedWarehouse?.warehouse_id || null;


    const onSubmit = async (data) => {

        const url = Base_Url + updateB2b + "/" + data.order_id;
        try {
            setIsLoading(true);
            setError(null);

            // Construct pickup location object
            const pickupLocation = {
                consignee_name: data.pickup_consignee_name,
                address: data.pickup_address,
                city: data.pickup_city,
                state: data.pickup_state,
                zip: data.pickup_zip,
                phone: data.pickup_phone,
            };

            // Construct dropoff location object
            const dropoffLocation = {
                consignee_name: data.dropoff_consignee_name,
                address: data.dropoff_address,
                city: data.dropoff_city,
                state: data.dropoff_state,
                zip: data.dropoff_zip,
                phone: data.dropoff_phone,
                email: data.dropoff_email
            };

            // Construct billing address object
            const billingAddress = {
                name: data.billing_name,
                company: data.billing_company,
                consignor: data.billing_consignor,
                address: data.billing_address,
                city: data.billing_city,
                state: data.billing_state,
                pin: data.billing_pin,
                phone: data.billing_phone,
                pan_number: data.billing_pan_number,
                gst_number: data.billing_gst_number
            };

            // Construct invoice array
            const invoices = [{
                ewaybill: data.invoice_ewaybill,
                inv_num: data.invoice_num,
                inv_amt: parseFloat(data.invoice_amt) || 0,
                inv_qr_code: data.invoice_qr_code || data.shipment_order_id
            }];
            const docData = [
                {
                    doc_type: "INVOICE_COPY",
                    doc_meta: {
                        invoice_num: [data.invoice_num]
                    }
                }
            ];


            // Construct shipment details array

            const shipmentDetails = [{
                order_id: data.shipment_order_id,
                box_count: data.shipment_box_count || 0,
                description: data.shipment_description,
                weight: data.shipment_weight || 0,
                waybills: [],
                master: data.shipment_master
            }];


            const dimensions = [
                {
                    box_count: parseInt(data.total_boxes) || 0,
                    length: parseFloat(data.total_length) || 0,
                    width: parseFloat(data.total_width) || 0,
                    height: parseFloat(data.total_height) || 0
                }
            ];
            const payload = {
                order_id: data.order_id,
                warehouse_id: warehouseId,
                retailer_id: shipment.user_id,
                lrn: data.lrn,
                payment_mode: data.payment_mode,
                cod_amount: data.payment_mode === "prepaid" ? 0 : data.cod_amount,
                payment_id: data.payment_id,    
                total_boxes: data.total_boxes,
                total_weight: data.total_weight,
                weight: data.total_weight,
                total_length: data.total_length,
                total_width: data.total_width,
                total_height: data.total_height,
                rov_insurance: data.rov_insurance,
                freight_mode: data.freight_mode,
                shipment_type: data.shipment_type,
                insurence: data.insurence,
                dimensions: JSON.stringify(dimensions),
                pickup_location: JSON.stringify(pickupLocation),
                dropoff_location: JSON.stringify(dropoffLocation),
                billing_address: JSON.stringify(billingAddress),
                invoices: JSON.stringify(invoices),
                doc_file: shipment.doc_file_path,
                doc_data: JSON.stringify(docData),
                shipment_details: JSON.stringify(shipmentDetails)

            };

            // Add cod_amount if payment mode is not prepaid
            if (data.payment_mode !== 'prepaid') {
                payload.cod_amount = data.cod_amount;
            }

            // Call API to update shipment
            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            if (response.data.status === "SUCCESS") {
                toast.success(response.data.message);
                setIsEditing(false);
                onClose();
                fetchOrderData && fetchOrderData();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update shipment');
            toast.error(err.response?.data?.message || 'Failed to update shipment');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    // Render an input field - either editable or read-only
    const renderField = (label, fieldName, type = 'text', isNumeric = false, placeholder = '') => {
        const editable = isEditable(fieldName);
        let value = '';

        // Handle different data sources based on field name
        if (fieldName.startsWith('pickup_') && shipment?.pickup_location) {
            try {
                const pickup = JSON.parse(shipment.pickup_location);
                const key = fieldName.replace('pickup_', '');
                value = pickup[key] || '';
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('dropoff_') && shipment?.dropoff_location) {
            try {
                const dropoff = JSON.parse(shipment.dropoff_location);
                const key = fieldName.replace('dropoff_', '');
                value = dropoff[key] || '';
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('billing_') && shipment?.billing_address) {
            try {
                const billing = JSON.parse(shipment.billing_address);
                const key = fieldName.replace('billing_', '');
                value = billing[key] || '';
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('invoice_') && shipment?.invoices) {
            try {
                const invoices = JSON.parse(shipment.invoices);
                if (invoices.length > 0) {
                    const key = fieldName.replace('invoice_', '');
                    // Map some fields
                    if (key === 'amt') {
                        value = invoices[0].inv_amt || '';
                    } else if (key === 'num') {
                        value = invoices[0].inv_num || '';
                    } else if (key === 'qr_code') {
                        value = invoices[0].inv_qr_code || '';
                    } else {
                        value = invoices[0][key] || '';
                    }
                }
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('shipment_') && shipment?.shipment_details) {
            try {
                const shipmentDetails = JSON.parse(shipment.shipment_details);
                if (shipmentDetails.length > 0) {
                    const key = fieldName.replace('shipment_', '');
                    if (key === 'order_id') {
                        value = shipmentDetails[0].order_id || '';
                    } else if (key === 'box_count') {
                        value = shipmentDetails[0].box_count || '';
                    } else if (key === 'description') {
                        value = shipmentDetails[0].description || '';
                    } else if (key === 'weight') {
                        value = shipmentDetails[0].weight || '';
                    } else if (key === 'master') {
                        value = shipmentDetails[0].master || '';
                    }
                }
            } catch (e) {
                value = '';
            }
        } else {
            // Direct field from shipment
            value = shipment?.[fieldName] || '';
            if (fieldName === 'cod_amount' && !value && shipment?.cod) {
                value = shipment.cod;
            }
        }




        return (
            <div>
                <label className="block text-sm ml-2 font-fans font-semibold text-black dark:text-white">{label}</label>
                {editable ? (
                    <>
                        <input
                            type={type}
                            step={isNumeric ? "0.1" : undefined}
                            placeholder={placeholder}
                            className={`mt-2 p-2 border w-full rounded-custom dark:shadow-custom-dark shadow-custom-light focus:ring-1 focus:ring-purple-500 ${isDarkMode ? "dark:bg-darkmodeColor dark:placeholder-white text-white" : ""
                                }`}
                            {...register(fieldName, isNumeric ? { valueAsNumber: true } : {})}
                        />
                        {errors[fieldName] && <p className="text-red-500 text-sm">{errors[fieldName].message}</p>}
                    </>
                ) : (
                    <div className={`mt-2 p-3 rounded-lg border-l-4 ${isDarkMode
                        ? "bg-gradient-to-r from-slate-800 to-slate-700 border-l-customPurple  text-white"
                        : "bg-gradient-to-r from-blue-50 to-slate-50 border-l-customPurple text-gray-800"
                        }`}>
                        <div className="flex items-center">
                            <span className="font-semibold text-lg">{value || 'Not specified'}</span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const handleClose = () => {
        // setIsEditing(false); // Reset edit mode when closing
        // reset();
        onClose();
    };

    if (!shipment) return null;

    return (
        <Dialog
            open={isopen}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                className: "rounded-lg overflow-hidden"
            }}
        >
            <DialogTitle className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-lg">
                <h2 className="text-sm sm:text-sm text-white font-sans flex items-center">
                    <FaWarehouse className="mr-3" size={16} />
                    B2B Shipment Details
                </h2>
                <IoIosCloseCircle
                    className="absolute top-3 right-3 cursor-pointer hover:scale-110 transition-transform"
                    size={20}
                    onClick={handleClose}
                />
            </DialogTitle>

            <DialogContent className="bg-gray-50 dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark custom-scrollbar" style={{ maxHeight: '75vh' }}>
                {/* {error && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                        <div className="flex items-center">
                            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </div>
                    </div>
                )} */}

                {!isEditing && (
                    <div className="flex justify-end mb-4">
                        <button
                            type="button"
                            onClick={handleEdit}
                            disabled={shipment?.status !== 'Pending'}
                            className={`py-2 px-4 rounded-lg mt-4 focus:outline-none focus:ring-2 focus:ring-opacity-50 font-medium transition-colors flex items-center ${shipment?.status === 'Pending'
                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 focus:ring-blue-500'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            <FaEdit className="mr-2" /> Edit
                        </button>
                    </div>
                )}

                <form className="space-y-8 font-sans mt-4" onSubmit={handleSubmit(onSubmit, onErrors)}>
                    {/* Basic Shipment Information */}
                    <div className="bg-purple-50 bg-white dark:bg-darkmodeColor p-3 rounded-custom shadow-custom-light dark:shadow-custom-dark border-l-4 border-purple-500">
                        <h3 className="text-lg font-semibold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
                            <FaBox className="mr-2" />
                            Basic Shipment Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Order ID', 'order_id')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('LRN', 'lrn')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Total Boxes', 'total_boxes', 'number', true)}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Total Weight', 'total_weight', 'number', true)}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('ROV Insurance', 'rov_insurance', 'number', true)}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Total Length', 'total_length', 'number', true)}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Total Width', 'total_width', 'number', true)}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Total Height', 'total_height', 'number', true)}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Freight Mode', 'freight_mode')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Shipment Type', 'shipment_type')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Insurance', 'insurence')}
                            </div>
                        </div>
                    </div>

                    {/* Pickup Location Section */}
                    <div className="bg-blue-50 dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark rounded-custom p-4 border-l-4 border-blue-500">
                        <h3 className="text-lg font-semibold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
                            <FaTruck className="mr-2" />
                            Pickup Location
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Consignee Name', 'pickup_consignee_name')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Phone', 'pickup_phone')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Address', 'pickup_address')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('City', 'pickup_city')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('State', 'pickup_state')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('ZIP Code', 'pickup_zip')}
                            </div>
                        </div>
                    </div>

                    {/* Dropoff Location Section */}
                    <div className="bg-green-50 dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark rounded-custom p-4 border-l-4 border-green-500">
                        <h3 className="text-lg font-semibold mb-4 text-green-700 dark:text-green-300 flex items-center">
                            <FaMapMarkerAlt className="mr-2" />
                            Dropoff Location
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Consignee Name', 'dropoff_consignee_name')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Phone', 'dropoff_phone')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Address', 'dropoff_address')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Email', 'dropoff_email')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('City', 'dropoff_city')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('State', 'dropoff_state')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('ZIP Code', 'dropoff_zip')}
                            </div>
                        </div>
                    </div>

                    {/* Billing Address Section */}
                    <div className="bg-yellow-50 dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-yellow-500">
                        <h3 className="text-lg font-semibold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
                            <FaFileInvoice className="mr-2" />
                            Billing Address
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Name', 'billing_name')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Company', 'billing_company')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Consignor', 'billing_consignor')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Phone', 'billing_phone')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Address', 'billing_address')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('City', 'billing_city')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('State', 'billing_state')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('PIN Code', 'billing_pin')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('PAN Number', 'billing_pan_number')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('GST Number', 'billing_gst_number')}
                            </div>
                        </div>
                    </div>

                    {/* Payment Details Section */}
                    <div className="bg-indigo-50 dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-indigo-500">
                        <h3 className="text-lg font-semibold mb-4 text-indigo-700 dark:text-indigo-300 flex items-center">
                            <FaCreditCard className="mr-2" />
                            Payment Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Payment ID', 'payment_id')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                <label className="block text-sm ml-2 font-fans font-semibold text-black dark:text-white">Payment Mode</label>
                                {isEditable('payment_mode') ? (
                                    <>
                                        <div className="relative">
                                            <select
                                                {...register('payment_mode')}
                                                className="block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white dark:bg-darkmodeColor dark:text-white dark:border-gray-600"
                                            >
                                                <option value="">Select Mode</option>
                                                <option value="cod">COD</option>
                                                <option value="prepaid">Prepaid</option>
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                <FaAngleDown className="text-gray-500" />
                                            </div>
                                        </div>
                                        {errors.payment_mode && (
                                            <p className="text-red-500 text-sm mt-1">{errors.payment_mode.message}</p>
                                        )}
                                    </>
                                ) : (
                                    <div className={`mt-2 p-3 rounded-lg border-l-4 ${isDarkMode
                                        ? "bg-gradient-to-r from-customPurple to-purple-700 border-l-customPurple text-white"
                                        : "bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"
                                        }`}>
                                        <span className="font-semibold text-lg">{shipment?.payment_mode || 'Not specified'}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* COD Amount Field (Shown only if payment_mode is 'cod') */}
                        {paymentMode === 'cod' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                                <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                    {renderField('COD Amount', 'cod_amount', 'number', true)}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Invoice Details Section */}
                    <div className="bg-orange-50 dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-orange-500">
                        <h3 className="text-lg font-semibold mb-4 text-orange-700 dark:text-orange-300 flex items-center">
                            <FaFileInvoice className="mr-2" />
                            Invoice Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('E-way Bill', 'invoice_ewaybill')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Invoice Number', 'invoice_num')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Invoice Amount', 'invoice_amt', 'number', true)}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('QR Code', 'invoice_qr_code')}
                            </div>
                        </div>
                    </div>

                    {/* Shipment Details Section */}
                    <div className="bg-teal-50 dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-teal-500">
                        <h3 className="text-lg font-semibold mb-4 text-teal-700 dark:text-teal-300 flex items-center">
                            <FaBoxOpen className="mr-2" />
                            Shipment Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Shipment Order ID', 'shipment_order_id')}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Box Count', 'shipment_box_count', 'number', true)}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Description', 'shipment_description')}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Weight', 'shipment_weight', 'number', true)}
                            </div>
                            <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
                                {renderField('Master', 'shipment_master')}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap justify-end gap-4 mt-8">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="w-full sm:w-auto bg-gray-500 text-white py-3 px-6 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 font-medium transition-colors flex items-center justify-center"
                        >
                            <FaTimes className="mr-2" /> Cancel
                        </button>
                        {isEditing && (
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`
                                    w-full sm:w-auto 
                                    bg-gradient-to-r from-purple-600 to-indigo-700 
                                    text-white py-3 px-6 rounded-lg 
                                    hover:from-purple-700 hover:to-indigo-800 
                                    focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 
                                    font-medium transition-colors flex items-center justify-center shadow-md
                                    disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
                                `}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="mr-2" /> Save Changes
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default B2BViewAllShipment;




// import { useEffect, useState } from 'react';
// import { Dialog, DialogTitle, DialogContent } from '@mui/material';
// import { z } from 'zod';
// import { FaBox, FaCreditCard, FaAngleDown, FaBoxOpen, FaTruck, FaTimes, FaSave } from 'react-icons/fa';
// import { FaMapMarkerAlt, FaFileInvoice } from 'react-icons/fa';
// import axios from 'axios';
// import { MdDelete } from "react-icons/md";
// import { useForm } from 'react-hook-form';
// import { IoIosCloseCircle } from "react-icons/io";
// import { zodResolver } from '@hookform/resolvers/zod';
// import { useMyContext } from '../../../context/MyContext';
// import { FaWarehouse } from "react-icons/fa6";
// import '../../css/custom-scrollbar.css';
// import { Base_Url, updateB2b, b2bWarehouseListAll } from '../../../config/config';
// import { toast } from 'react-toastify';
// import { FaEdit } from 'react-icons/fa';

// const schema = z.object({
//     // Basic Shipment Information
//     order_id: z.string()
//         .min(1, "Order ID is required")
//         .regex(/^[A-Za-z0-9-_]+$/, "Order ID must contain only alphanumeric characters, hyphens, and underscores"),

//     // lrn: z.string()
//     //     .min(1, "LRN is required")
//     //     .regex(/^[A-Za-z0-9-_]+$/, "LRN must contain only alphanumeric characters, hyphens, and underscores"),

//     lrn: z.string().optional(),
//     total_boxes: z.number()
//         .min(1, "Total boxes must be at least 1")
//         .int("Total boxes must be a whole number"),

//     total_weight: z.number()
//         .min(0.1, "Total weight must be greater than 0")
//         .max(1000000, "Total weight cannot exceed 1,000,000 kg"),

//     rov_insurance: z.number()
//         .min(0, "ROV Insurance cannot be negative")
//         .optional(),

//     total_length: z.number()
//         .min(0.1, "Total length must be greater than 0")
//         .max(10000, "Total length cannot exceed 10,000 cm"),

//     total_width: z.number()
//         .min(0.1, "Total width must be greater than 0")
//         .max(10000, "Total width cannot exceed 10,000 cm"),

//     total_height: z.number()
//         .min(0.1, "Total height must be greater than 0")
//         .max(10000, "Total height cannot exceed 10,000 cm"),

//     freight_mode: z.string()
//         .min(1, "Freight mode is required")
//         .regex(/^[A-Za-z\s]+$/, "Freight mode must only contain letters and spaces"),

//     shipment_type: z.string().optional(),
//     // .min(1, "Shipment type is required")
//     // .regex(/^[A-Za-z\s]+$/, "Shipment type must only contain letters and spaces"),


//     shipment_order_id: z.string().min(1, "shipment_order_id is required"),
//     shipment_weight: z
//         .number()
//         .positive("Weight must be greater than 0"),
//     shipment_description: z.string().min(1, "shipment_description is required"),
//     shipment_box_count: z
//         .number()
//         .positive("Box count must be greater than 0"),
//     // shipment_weight: z
//     //     .number()
//     //     .positive("Weight must be greater than 0"),

//     insurence: z.string().optional(), // Note: keeping the typo as in original form

//     // Pickup Location
//     pickup_consignee_name: z.string()
//         .min(1, "Pickup consignee name is required")
//         .min(2, "Name must be at least 2 characters")
//         .max(100, "Name cannot exceed 100 characters"),
//     // .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     pickup_phone: z.string()
//         .min(10, "Phone number must be at least 10 digits")
//         .max(10, "Phone number cannot exceed 10 digits")
//         .regex(/^[\+]?[\d\s\-\(\)]+$/, "Phone number format is invalid"),

//     pickup_address: z.string()
//         .min(10, "Pickup address must be at least 10 characters")
//         .max(200, "Address cannot exceed 200 characters"),

//     pickup_city: z.string()
//         .min(1, "Pickup city is required")
//         .min(2, "City must be at least 2 characters")
//         .max(50, "City cannot exceed 50 characters")
//         .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     pickup_state: z.string()
//         .min(1, "Pickup state is required")
//         .min(2, "State must be at least 2 characters")
//         .max(50, "State cannot exceed 50 characters")
//         .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     pickup_zip: z.string()
//         .min(1, "Pickup ZIP code is required")
//         .regex(/^[A-Za-z0-9\s-]+$/, "ZIP code format is invalid")
//         .min(3, "ZIP code must be at least 3 characters")
//         .max(10, "ZIP code cannot exceed 10 characters"),

//     // Dropoff Location
//     dropoff_consignee_name: z.string()
//         .min(1, "Dropoff consignee name is required")
//         .min(2, "Name must be at least 2 characters")
//         .max(100, "Name cannot exceed 100 characters"),
//     // .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     dropoff_phone: z.string()
//         .min(10, "Phone number must be at least 10 digits")
//         .max(15, "Phone number cannot exceed 15 digits")
//         .regex(/^[\+]?[\d\s\-\(\)]+$/, "Phone number format is invalid"),

//     dropoff_address: z.string()
//         .min(10, "Dropoff address must be at least 10 characters")
//         .max(200, "Address cannot exceed 200 characters"),

//     dropoff_email: z.string()
//         .email("Invalid email address")
//         .optional()
//         .or(z.literal("")),

//     dropoff_city: z.string()
//         .min(1, "Dropoff city is required")
//         .min(2, "City must be at least 2 characters")
//         .max(50, "City cannot exceed 50 characters")
//         .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     dropoff_state: z.string()
//         .min(1, "Dropoff state is required")
//         .min(2, "State must be at least 2 characters")
//         .max(50, "State cannot exceed 50 characters")
//         .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     dropoff_zip: z.string()
//         .min(1, "Dropoff ZIP code is required")
//         .regex(/^[A-Za-z0-9\s-]+$/, "ZIP code format is invalid")
//         .min(3, "ZIP code must be at least 3 characters")
//         .max(10, "ZIP code cannot exceed 10 characters"),

//     // Billing Address
//     billing_name: z.string()
//         .min(1, "Billing name is required")
//         .min(2, "Name must be at least 2 characters")
//         .max(100, "Name cannot exceed 100 characters")
//         .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     billing_company: z.string()
//         .min(1, "Company name is required")
//         .min(2, "Company name must be at least 2 characters")
//         .max(100, "Company name cannot exceed 100 characters"),

//     billing_consignor: z.string()
//         .min(1, "Consignor is required")
//         .min(2, "Consignor must be at least 2 characters")
//         .max(100, "Consignor cannot exceed 100 characters")
//         .regex(/^[a-zA-Z\s.'-]+$/, "Consignor must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     billing_phone: z.string()
//         .min(10, "Phone number must be at least 10 digits")
//         .max(15, "Phone number cannot exceed 15 digits")
//         .regex(/^[\+]?[\d\s\-\(\)]/, "Phone number format is invalid"),

//     billing_address: z.string()
//         .min(10, "Billing address must be at least 10 characters")
//         .max(200, "Address cannot exceed 200 characters"),

//     billing_city: z.string()
//         .min(1, "Billing city is required")
//         .min(2, "City must be at least 2 characters")
//         .max(50, "City cannot exceed 50 characters")
//         .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     billing_state: z.string()
//         .min(1, "Billing state is required")
//         .min(2, "State must be at least 2 characters")
//         .max(50, "State cannot exceed 50 characters")
//         .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),

//     billing_pin: z.string()
//         .min(1, "PIN code is required")
//         .regex(/^[A-Za-z0-9\s-]+$/, "PIN code format is invalid")
//         .min(3, "PIN code must be at least 3 characters")
//         .max(10, "PIN code cannot exceed 10 characters"),

//     billing_pan_number: z.string()
//         .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "PAN number must be in format: ABCDE1234F")
//         .optional()
//         .or(z.literal("")),

//     billing_gst_number: z.string()
//         .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "GST number format is invalid")
//         .optional()
//         .or(z.literal("")),

//     // Payment Details
//     payment_id: z.string().optional(),
//     // .min(1, "Payment ID is required")
//     // .regex(/^[A-Za-z0-9-_]+$/, "Payment ID must contain only alphanumeric characters, hyphens, and underscores"),

//     // payment_mode: z.enum([
//     //     "COD",
//     //     "Prepaid",
//     // ], {
//     //     errorMap: () => ({ message: "Please select a valid payment mode" })
//     // }),
//     invoice_num: z.string().optional(),
//     payment_mode: z.string().optional(),
//     inv_amt: z.string().optional(),

// });



// const B2BViewAllShipment = ({ isopen, onClose, shipment, fetchOrderData, wareHouses = [] }) => {
//     const { isDarkMode } = useMyContext();
//     const [isLoading, setIsLoading] = useState(false);
//     const [error, setError] = useState(null);
//     const [isEditing, setIsEditing] = useState(false);
//     const token = localStorage.getItem('token');
//     const cus_id = localStorage.getItem('cus_id');
//     const [dimensions, setDimensions] = useState([]);

//     useEffect(() => {
//         if (shipment?.dimensions) {
//             const parsedDimensions = JSON.parse(shipment.dimensions);
//             setDimensions(parsedDimensions);
//         }
//     }, [shipment]);

//     const handleChange = (index, field, value) => {
//         const updated = [...dimensions];
//         updated[index][field] = value;
//         setDimensions(updated);
//     };

//     const addBox = () => {
//         setDimensions([
//             ...dimensions,
//             { box_count: 1, length: "", width: "", height: "" }
//         ]);
//     };

//     const deleteBox = (index) => {
//         const updated = dimensions.filter((_, i) => i !== index);
//         setDimensions(updated);
//     };

//     const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
//         resolver: zodResolver(schema),
//         defaultValues: {
//             order_id: '',
//             lrn: '',
//             payment_mode: '',
//             payment_id: '',
//             cod_amount: '',
//             total_boxes: '',
//             total_weight: '',
//             total_length: '',
//             total_width: '',
//             total_height: '',
//             rov_insurance: '',
//             freight_mode: '',
//             shipment_type: '',
//             insurence: '',
//             // Pickup location fields
//             pickup_consignee_name: '',
//             pickup_address: '',
//             pickup_city: '',
//             pickup_state: '',
//             pickup_zip: '',
//             pickup_phone: '',
//             // Dropoff location fields
//             dropoff_consignee_name: '',
//             dropoff_address: '',
//             dropoff_city: '',
//             dropoff_state: '',
//             dropoff_zip: '',
//             dropoff_phone: '',
//             dropoff_email: '',
//             // Billing address fields
//             billing_name: '',
//             billing_company: '',
//             billing_consignor: '',
//             billing_address: '',
//             billing_city: '',
//             billing_state: '',
//             billing_pin: '',
//             billing_phone: '',
//             billing_pan_number: '',
//             billing_gst_number: '',
//             // Invoice fields
//             invoice_ewaybill: '',
//             invoice_num: '',
//             invoice_amt: '',
//             invoice_qr_code: '',
//             // Shipment details
//             shipment_order_id: '',
//             shipment_box_count: '',
//             shipment_description: '',
//             shipment_weight: '',
//             shipment_master: '',

//         }
//     });
//     const paymentMode = watch('payment_mode');

//     // Check if a field is editable
//     const isEditable = (fieldName) => {
//         const editableFields = [
//             'lrn', 'payment_mode', 'payment_id', 'cod_amount', 'total_boxes',
//             'total_weight', 'total_length', 'total_width', 'total_height',
//             'rov_insurance', 'freight_mode', 'shipment_type', 'insurence',
//             'pickup_consignee_name', 'pickup_address', 'pickup_city', 'pickup_state',
//             'pickup_zip', 'pickup_phone', 'dropoff_consignee_name', 'dropoff_address',
//             'dropoff_city', 'dropoff_state', 'dropoff_zip', 'dropoff_phone',
//             'dropoff_email', 'billing_name', 'billing_company', 'billing_consignor',
//             'billing_address', 'billing_city', 'billing_state', 'billing_pin',
//             'billing_phone', 'billing_pan_number', 'billing_gst_number',
//             'invoice_ewaybill', 'invoice_num', 'invoice_amt', 'invoice_qr_code',
//             'shipment_order_id', 'shipment_box_count', 'shipment_description',
//             'shipment_weight', 'shipment_master'
//         ];
//         return editableFields.includes(fieldName) && isEditing; 
//     };

    
//     useEffect(() => {
//         if (shipment) {
//             try {
//                 // Parse JSON string fields
//                 const pickup = shipment.pickup_location ? JSON.parse(shipment.pickup_location) : {};
//                 const dropoff = shipment.dropoff_location ? JSON.parse(shipment.dropoff_location) : {};
//                 const billing = shipment.billing_address ? JSON.parse(shipment.billing_address) : {};
//                 const invoices = shipment.invoices ? JSON.parse(shipment.invoices) : [];
//                 const shipmentDetails = shipment.shipment_details ? JSON.parse(shipment.shipment_details) : [];

//                 // Set basic shipment fields
//                 setValue('order_id', shipment.order_id || '');
//                 setValue('lrn', shipment.lrn || '');
//                 setValue('payment_mode', shipment.payment_mode || '');
//                 setValue('payment_id', shipment.payment_id || '');
//                 setValue('cod_amount', shipment.cod_amount || '');
//                 setValue('total_boxes', shipment.total_boxes || '');
//                 setValue('total_weight', shipment.total_weight || '');
//                 setValue('total_length', shipment.total_length || '');
//                 setValue('total_width', shipment.total_width || '');
//                 setValue('total_height', shipment.total_height || '');
//                 setValue('rov_insurance', shipment.rov_insurance || '');
//                 setValue('freight_mode', shipment.freight_mode || '');
//                 setValue('shipment_type', shipment.shipment_type || '');
//                 setValue('insurence', shipment.insurence || '');

//                 // Set pickup location fields
//                 setValue('pickup_consignee_name', pickup.consignee_name || '');
//                 setValue('pickup_address', pickup.address || '');
//                 setValue('pickup_city', pickup.city || '');
//                 setValue('pickup_state', pickup.state || '');
//                 setValue('pickup_zip', pickup.zip || '');
//                 setValue('pickup_phone', pickup.phone || '');

//                 // Set dropoff location fields
//                 setValue('dropoff_consignee_name', dropoff.consignee_name || '');
//                 setValue('dropoff_address', dropoff.address || '');
//                 setValue('dropoff_city', dropoff.city || '');
//                 setValue('dropoff_state', dropoff.state || '');
//                 setValue('dropoff_zip', dropoff.zip || '');
//                 setValue('dropoff_phone', dropoff.phone || '');
//                 setValue('dropoff_email', dropoff.email || '');

//                 // Set billing address fields
//                 setValue('billing_name', billing.name || '');
//                 setValue('billing_company', billing.company || '');
//                 setValue('billing_consignor', billing.consignor || '');
//                 setValue('billing_address', billing.address || '');
//                 setValue('billing_city', billing.city || '');
//                 setValue('billing_state', billing.state || '');
//                 setValue('billing_pin', billing.pin || '');
//                 setValue('billing_phone', billing.phone || '');
//                 setValue('billing_pan_number', billing.pan_number || '');
//                 setValue('billing_gst_number', billing.gst_number || '');

//                 // Set invoice fields (first invoice if exists)
//                 if (invoices.length > 0) {
//                     const firstInvoice = invoices[0];
//                     setValue('invoice_ewaybill', firstInvoice.ewaybill || '');
//                     setValue('invoice_num', firstInvoice.inv_num || '');
//                     setValue('invoice_amt', firstInvoice.inv_amt || '');
//                     setValue('invoice_qr_code', firstInvoice.inv_qr_code || '');
//                 }

//                 // Set shipment details (first shipment if exists)
//                 if (shipmentDetails.length > 0) {
//                     const firstShipment = shipmentDetails[0];
//                     setValue('shipment_order_id', firstShipment.order_id || '');
//                     setValue('shipment_box_count', firstShipment.box_count || '');
//                     setValue('shipment_description', firstShipment.description || '');
//                     setValue('shipment_weight', firstShipment.weight || '');
//                     setValue('shipment_master', firstShipment.master || '');
//                 }

//             } catch (error) {
//                 console.error('Error parsing shipment data:', error);
//                 setError('Error parsing shipment data');
//             }
//         }
//     }, [shipment, setValue]);

//     const onErrors = (errors) => {
//         toast.error("Please fix the errors in the form.");
//     };

//     let pickupLocation = {};
//     try {
//         pickupLocation = shipment?.pickup_location
//             ? JSON.parse(shipment.pickup_location)
//             : {};
//     } catch (error) {
//         console.error("Invalid pickup_location JSON", error);
//     }

//     const WarehouseConsignee = pickupLocation?.consignee_name?.trim();

//     const matchedWarehouse = wareHouses.find(
//         (wh) =>
//             wh?.name?.trim().toLowerCase() ===
//             WarehouseConsignee?.toLowerCase()
//     );

//     const warehouseId = matchedWarehouse?.warehouse_id || null;

//     const onSubmit = async (data) => {
//         const url = Base_Url + updateB2b + "/" + data.order_id;
//         try {
//             setIsLoading(true);
//             setError(null);

//             // Construct pickup location object
//             const pickupLocation = {
//                 consignee_name: data.pickup_consignee_name,
//                 address: data.pickup_address,
//                 city: data.pickup_city,
//                 state: data.pickup_state,
//                 zip: data.pickup_zip,
//                 phone: data.pickup_phone,
//             };

//             // Construct dropoff location object
//             const dropoffLocation = {
//                 consignee_name: data.dropoff_consignee_name,
//                 address: data.dropoff_address,
//                 city: data.dropoff_city,
//                 state: data.dropoff_state,
//                 zip: data.dropoff_zip,
//                 phone: data.dropoff_phone,
//                 email: data.dropoff_email
//             };

//             // Construct billing address object
//             const billingAddress = {
//                 name: data.billing_name,
//                 company: data.billing_company,
//                 consignor: data.billing_consignor,
//                 address: data.billing_address,
//                 city: data.billing_city,
//                 state: data.billing_state,
//                 pin: data.billing_pin,
//                 phone: data.billing_phone,
//                 pan_number: data.billing_pan_number,
//                 gst_number: data.billing_gst_number
//             };

//             // Construct invoice array
//             const invoices = [{
//                 ewaybill: data.invoice_ewaybill,
//                 inv_num: data.invoice_num,
//                 inv_amt: parseFloat(data.invoice_amt) || 0,
//                 inv_qr_code: data.invoice_qr_code || data.shipment_order_id
//             }];
//             const docData = [
//                 {
//                     doc_type: "INVOICE_COPY",
//                     doc_meta: {
//                         invoice_num: [data.invoice_num]
//                     }
//                 }
//             ];


//             // Construct shipment details array

//             const shipmentDetails = [{
//                 order_id: data.shipment_order_id,
//                 box_count: data.shipment_box_count || 0,
//                 description: data.shipment_description,
//                 weight: data.shipment_weight || 0,
//                 waybills: [],
//                 master: data.shipment_master
//             }];


//             // const dimensions = [
//             //     {
//             //         box_count: parseInt(data.total_boxes) || 0,
//             //         length: parseFloat(data.total_length) || 0,
//             //         width: parseFloat(data.total_width) || 0,
//             //         height: parseFloat(data.total_height) || 0
//             //     }
//             // ];

//             const payload = {
//                 order_id: data.order_id,
//                 warehouse_id: warehouseId,
//                 retailer_id: shipment.user_id,
//                 lrn: data.lrn,
//                 payment_mode: data.payment_mode,
//                 cod_amount: data.payment_mode === "prepaid" ? 0 : data.cod_amount,
//                 payment_id: data.payment_id,
//                 total_boxes: data.total_boxes,
//                 total_weight: data.total_weight,
//                 weight: data.total_weight,
//                 total_length: data.total_length,
//                 total_width: data.total_width,
//                 total_height: data.total_height,
//                 rov_insurance: data.rov_insurance,
//                 freight_mode: data.freight_mode,
//                 shipment_type: data.shipment_type,
//                 insurence: data.insurence,
//                 // dimensions: JSON.stringify(dimensions),
//                 dimensions: JSON.stringify(
//                     dimensions.map(box => ({
//                         box_count: Number(box.box_count),
//                         length: Number(box.length),
//                         width: Number(box.width),
//                         height: Number(box.height)
//                     }))
//                 ),
//                 pickup_location: JSON.stringify(pickupLocation),
//                 dropoff_location: JSON.stringify(dropoffLocation),
//                 billing_address: JSON.stringify(billingAddress),
//                 invoices: JSON.stringify(invoices),
//                 doc_file: shipment.doc_file_path,
//                 doc_data: JSON.stringify(docData),
//                 shipment_details: JSON.stringify(shipmentDetails)

//             };

//             // Add cod_amount if payment mode is not prepaid
//             if (data.payment_mode !== 'prepaid') {
//                 payload.cod_amount = data.cod_amount;
//             }

//             // Call API to update shipment
//             const response = await axios.post(url, payload, {
//                 headers: {
//                     'Content-Type': 'application/json',
//                     'token': token,
//                     'cus_id': cus_id,
//                 },
//             });

//             if (response.data.status === "SUCCESS") {
//                 toast.success(response.data.message);
//                 setIsEditing(false);
//                 onClose();
//                 fetchOrderData && fetchOrderData();
//             }
//         } catch (err) {
//             setError(err.response?.data?.message || 'Failed to update shipment');
//             toast.error(err.response?.data?.message || 'Failed to update shipment');
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     const handleEdit = () => {
//         setIsEditing(true);
//     };

//     // Render an input field - either editable or read-only
//     const renderField = (label, fieldName, type = 'text', isNumeric = false, placeholder = '') => {
//         const editable = isEditable(fieldName);
//         let value = '';

//         // Handle different data sources based on field name
//         if (fieldName.startsWith('pickup_') && shipment?.pickup_location) {
//             try {
//                 const pickup = JSON.parse(shipment.pickup_location);
//                 const key = fieldName.replace('pickup_', '');
//                 value = pickup[key] || '';
//             } catch (e) {
//                 value = '';
//             }
//         } else if (fieldName.startsWith('dropoff_') && shipment?.dropoff_location) {
//             try {
//                 const dropoff = JSON.parse(shipment.dropoff_location);
//                 const key = fieldName.replace('dropoff_', '');
//                 value = dropoff[key] || '';
//             } catch (e) {
//                 value = '';
//             }
//         } else if (fieldName.startsWith('billing_') && shipment?.billing_address) {
//             try {
//                 const billing = JSON.parse(shipment.billing_address);
//                 const key = fieldName.replace('billing_', '');
//                 value = billing[key] || '';
//             } catch (e) {
//                 value = '';
//             }
//         } else if (fieldName.startsWith('invoice_') && shipment?.invoices) {
//             try {
//                 const invoices = JSON.parse(shipment.invoices);
//                 if (invoices.length > 0) {
//                     const key = fieldName.replace('invoice_', '');
//                     // Map some fields
//                     if (key === 'amt') {
//                         value = invoices[0].inv_amt || '';
//                     } else if (key === 'num') {
//                         value = invoices[0].inv_num || '';
//                     } else if (key === 'qr_code') {
//                         value = invoices[0].inv_qr_code || '';
//                     } else {
//                         value = invoices[0][key] || '';
//                     }
//                 }
//             } catch (e) {
//                 value = '';
//             }
//         } else if (fieldName.startsWith('shipment_') && shipment?.shipment_details) {
//             try {
//                 const shipmentDetails = JSON.parse(shipment.shipment_details);
//                 if (shipmentDetails.length > 0) {
//                     const key = fieldName.replace('shipment_', '');
//                     if (key === 'order_id') {
//                         value = shipmentDetails[0].order_id || '';
//                     } else if (key === 'box_count') {
//                         value = shipmentDetails[0].box_count || '';
//                     } else if (key === 'description') {
//                         value = shipmentDetails[0].description || '';
//                     } else if (key === 'weight') {
//                         value = shipmentDetails[0].weight || '';
//                     } else if (key === 'master') {
//                         value = shipmentDetails[0].master || '';
//                     }
//                 }
//             } catch (e) {
//                 value = '';
//             }
//         } else {
//             // Direct field from shipment
//             value = shipment?.[fieldName] || '';
//             if (fieldName === 'cod_amount' && !value && shipment?.cod) {
//                 value = shipment.cod;
//             }
//         }
//         return (
//             <div>
//                 <label className="block text-xs mb-1 font-medium">{label}</label>
//                 {editable ? (
//                     <>
//                         <input
//                             type={type}
//                             step={isNumeric ? "0.1" : undefined}
//                             placeholder={`Enter ${label}`}
//                             className={`p-1.5 text-xs appearance-none shadow-md rounded-custom border w-full focus:outline-none focus:shadow-outline ${isDarkMode
//                                 ? "bg-darkmodeColor border-gray-600 text-white placeholder-gray-500 focus:border-customPurple"
//                                 : "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-customPurple"
//                                 }`}
//                             {...register(fieldName, isNumeric ? { valueAsNumber: true } : {})}
//                         />
//                         {errors[fieldName] && <p className="text-red-500 text-xs mt-1">{errors[fieldName].message}</p>}
//                     </>
//                 ) : (
//                     <div className={`p-1.5 text-xs appearance-none shadow-md rounded-custom border w-full focus:outline-none focus:shadow-outline ${isDarkMode
//                         ? "border-gray-600 text-gray-100"
//                         : "bg-gray-100 border-gray-300 text-gray-800"
//                         }`}>
//                         <span className="text-xs">{value || 'Not specified'}</span>
//                     </div>
//                 )}
//             </div>
//         );
//     };


//     const handleClose = () => {
//         // setIsEditing(false); // Reset edit mode when closing
//         // reset();
//         onClose();
//     };

//     if (!shipment) return null;

//     return (
//         <Dialog
//             open={isopen}
//             onClose={handleClose}
//             maxWidth="lg"
//             fullWidth
//             PaperProps={{
//                 className: "rounded-lg overflow-hidden"
//             }}
//         >
//             <DialogTitle className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-lg">
//                 <h2 className="text-sm sm:text-sm text-white font-sans flex items-center">
//                     <FaWarehouse className="mr-3" size={16} />
//                     B2B Shipment Details
//                 </h2>
//                 <IoIosCloseCircle
//                     className="absolute top-3 right-3 cursor-pointer hover:scale-110 transition-transform"
//                     size={20}
//                     onClick={handleClose}
//                 />
//             </DialogTitle>
//             <DialogContent className={`sm:mt-1 overflow-auto transition-all duration-300 custom-scrollbar rounded-custom md:mx-4 md:mb-4 ${isDarkMode
//                 ? "dark:bg-darkmodeColor dark:text-white shadow-custom-dark"
//                 : "bg-white"
//                 }`}>
//                 {!isEditing && (
//                     <div className="flex justify-end mb-4">
//                         <button
//                             type="button"
//                             onClick={handleEdit}
//                             disabled={shipment?.status !== 'Pending'}
//                             className={`py-2 px-4 rounded-lg mt-4 focus:outline-none focus:ring-2 focus:ring-opacity-50 font-medium transition-colors flex items-center ${shipment?.status === 'Pending'
//                                 ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 focus:ring-blue-500'
//                                 : 'bg-white text-gray-500 cursor-not-allowed'
//                                 }`}
//                         >
//                             <FaEdit className="mr-2" /> Edit
//                         </button>
//                     </div>
//                 )}

//                 <form className="space-y-8 font-sans mt-4" onSubmit={handleSubmit(onSubmit, onErrors)}>
//                     {/* Basic Shipment Information */}
//                     <div className="bg-white dark:bg-darkmodeColor mt-4 p-3 rounded-custom shadow-md dark:shadow-custom-dark border-l-4 border-purple-500">
//                         <h3 className="text-sm font-semibold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
//                             <FaBox className="mr-2" />
//                             Basic Shipment Information
//                         </h3>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Order ID', 'order_id')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
//                                 {renderField('LRN', 'lrn')}
//                             </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Total Boxes', 'total_boxes', 'number', true)}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Total Weight', 'total_weight', 'number', true)}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('ROV Insurance', 'rov_insurance', 'number', true)}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Total Length', 'total_length', 'number', true)}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Total Width', 'total_width', 'number', true)}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Total Height', 'total_height', 'number', true)}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
//                                 {renderField('Freight Mode', 'freight_mode')}
//                             </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
//                                 {renderField('Shipment Type', 'shipment_type')}
//                             </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Insurance', 'insurence')}
//                             {/* </div> */}
//                         </div>
//                     </div>

//                     {/* Pickup Location Section */}
//                     <div className=" dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark rounded-custom p-4 border-l-4 border-blue-500">
//                         <h3 className="text-sm font-semibold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
//                             <FaTruck className="mr-2" />
//                             Pickup Location
//                         </h3>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Consignee Name', 'pickup_consignee_name')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Phone', 'pickup_phone')}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Address', 'pickup_address')}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('City', 'pickup_city')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('State', 'pickup_state')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('ZIP Code', 'pickup_zip')}
//                             {/* </div> */}
//                         </div>
//                     </div>

//                     {/* Dropoff Location Section */}
//                     <div className="dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark rounded-custom p-4 border-l-4 border-green-500">
//                         <h3 className="text-sm font-semibold mb-4 text-green-700 dark:text-green-300 flex items-center">
//                             <FaMapMarkerAlt className="mr-2" />
//                             Dropoff Location
//                         </h3>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Consignee Name', 'dropoff_consignee_name')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Phone', 'dropoff_phone')}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Address', 'dropoff_address')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Email', 'dropoff_email')}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('City', 'dropoff_city')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('State', 'dropoff_state')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('ZIP Code', 'dropoff_zip')}
//                             {/* </div> */}
//                         </div>
//                     </div>

//                     {/* Billing Address Section */}
//                     <div className=" dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-yellow-500">
//                         <h3 className="text-sm font-semibold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
//                             <FaFileInvoice className="mr-2" />
//                             Billing Address
//                         </h3>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Name', 'billing_name')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Company', 'billing_company')}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Consignor', 'billing_consignor')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Phone', 'billing_phone')}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Address', 'billing_address')}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('City', 'billing_city')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('State', 'billing_state')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('PIN Code', 'billing_pin')}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
//                                 {renderField('PAN Number', 'billing_pan_number')}
//                             </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
//                                 {renderField('GST Number', 'billing_gst_number')}
//                             </div> */}
//                         </div>
//                     </div>

//                     {/* Payment Details Section */}
//                     <div className="dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-indigo-500">
//                         <h3 className="text-sm font-semibold mb-4 text-indigo-700 dark:text-indigo-300 flex items-center">
//                             <FaCreditCard className="mr-2" />
//                             Payment Details
//                         </h3>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
//                                 {renderField('Payment ID', 'payment_id')}
//                             </div> */}
//                             <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
//                                 <label className="block text-sm ml-2 font-fans font-semibold text-black dark:text-white">Payment Mode</label>
//                                 {isEditable('payment_mode') ? (
//                                     <>
//                                         <div className="relative">
//                                             <select
//                                                 {...register('payment_mode')}
//                                                 className="block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white dark:bg-darkmodeColor dark:text-white dark:border-gray-600"
//                                             >
//                                                 <option value="">Select Mode</option>
//                                                 <option value="cod">COD</option>
//                                                 <option value="prepaid">Prepaid</option>
//                                             </select>
//                                             <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
//                                                 <FaAngleDown className="text-gray-500" />
//                                             </div>
//                                         </div>
//                                         {errors.payment_mode && (
//                                             <p className="text-red-500 text-sm mt-1">{errors.payment_mode.message}</p>
//                                         )}
//                                     </>
//                                 ) : (
//                                     <div className={`mt-2 p-3 rounded-lg border-l-4 ${isDarkMode
//                                         ? "bg-gradient-to-r from-customPurple to-purple-700 border-l-customPurple text-white"
//                                         : "bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"
//                                         }`}>
//                                         <span className="font-semibold text-lg">{shipment?.payment_mode || 'Not specified'}</span>
//                                     </div>
//                                 )}
//                             </div>
//                         </div>

//                         {/* COD Amount Field (Shown only if payment_mode is 'cod') */}
//                         {paymentMode === 'cod' && (
//                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
//                                 {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                                 {renderField('COD Amount', 'cod_amount', 'number', true)}
//                                 {/* </div> */}
//                             </div>
//                         )}
//                     </div>

//                     {/* Invoice Details Section */}
//                     <div className="dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-orange-500">
//                         <h3 className="text-sm font-semibold mb-4 text-orange-700 dark:text-orange-300 flex items-center">
//                             <FaFileInvoice className="mr-2" />
//                             Invoice Details
//                         </h3>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('E-way Bill', 'invoice_ewaybill')}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Invoice Number', 'invoice_num')}
//                             {/* </div> */}
//                         </div>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark"> */}
//                             {renderField('Invoice Amount', 'invoice_amt', 'number', true)}
//                             {/* </div> */}
//                             {/* <div className="bg-white dark:bg-darkmodeColor p-3 rounded-lg shadow-custom-light dark:shadow-custom-dark">
//                                 {renderField('QR Code', 'invoice_qr_code')}
//                             </div> */}
//                         </div>
//                     </div>

//                     {/* Shipment Details Section */}
//                     {/* <div className=" dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-teal-500"> */}
//                         {/* <h3 className="text-sm font-semibold mb-4 text-teal-700 dark:text-teal-300 flex items-center">
//                             <FaBoxOpen className="mr-2" />
//                             Shipment Details
//                         </h3> */}
//                         <div className="dark:bg-darkmodeColor shadow-custom-light dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-teal-500">
//                             <h3 className="text-sm font-semibold mb-4 text-teal-700 dark:text-teal-300 flex items-center">
//                                 <FaBoxOpen className="mr-2" />
//                                 Dimensions
//                             </h3>

//                             {dimensions.map((box, index) => (
//                                 <div
//                                     key={index}
//                                     className="bg-white dark:bg-darkmodeColor p-4 rounded-lg shadow-custom-light dark:shadow-custom-dark mb-4"
//                                 >
//                                     <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1.6fr_1.6fr_1.6fr_0.2fr] gap-4 items-end">

//                                         {/* Box Count */}
//                                         <div className="flex flex-col">
//                                             <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
//                                                 Box Count
//                                             </label>
//                                             <input
//                                                 type="number"
//                                                 value={box.box_count}
//                                                 onChange={(e) =>
//                                                     handleChange(index, "box_count", e.target.value)
//                                                 }
//                                                 className={`p-1.5 text-xs shadow-md rounded-custom border w-full focus:outline-none focus:shadow-outline
//             ${isDarkMode
//                                                         ? "bg-darkmodeColor border-gray-600 text-white"
//                                                         : "bg-white border-gray-300 text-gray-800"
//                                                     }`}
//                                             />
//                                         </div>

//                                         {/* Length */}
//                                         <div className="flex flex-col">
//                                             <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
//                                                 Length (cm)
//                                             </label>
//                                             <input
//                                                 type="number"
//                                                 value={box.length}
//                                                 onChange={(e) =>
//                                                     handleChange(index, "length", e.target.value)
//                                                 }
//                                                 className={`p-1.5 text-xs shadow-md rounded-custom border w-full focus:outline-none focus:shadow-outline
//             ${isDarkMode
//                                                         ? "bg-darkmodeColor border-gray-600 text-white"
//                                                         : "bg-white border-gray-300 text-gray-800"
//                                                     }`}
//                                             />
//                                         </div>

//                                         {/* Width */}
//                                         <div className="flex flex-col">
//                                             <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
//                                                 Width (cm)
//                                             </label>
//                                             <input
//                                                 type="number"
//                                                 value={box.width}
//                                                 onChange={(e) =>
//                                                     handleChange(index, "width", e.target.value)
//                                                 }
//                                                 className={`p-1.5 text-xs shadow-md rounded-custom border w-full focus:outline-none focus:shadow-outline
//             ${isDarkMode
//                                                         ? "bg-darkmodeColor border-gray-600 text-white"
//                                                         : "bg-white border-gray-300 text-gray-800"
//                                                     }`}
//                                             />
//                                         </div>

//                                         {/* Height */}
//                                         <div className="flex flex-col">
//                                             <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
//                                                 Height (cm)
//                                             </label>
//                                             <input
//                                                 type="number"
//                                                 value={box.height}
//                                                 onChange={(e) =>
//                                                     handleChange(index, "height", e.target.value)
//                                                 }
//                                                 className={`p-1.5 text-xs shadow-md rounded-custom border w-full focus:outline-none focus:shadow-outline
//             ${isDarkMode
//                                                         ? "bg-darkmodeColor border-gray-600 text-white"
//                                                         : "bg-white border-gray-300 text-gray-800"
//                                                     }`}
//                                             />
//                                         </div>

//                                         {/* Delete */}
//                                         <div className="flex justify-center pb-1">
//                                             <button
//                                                 type="button"
//                                                 onClick={() => deleteBox(index)}
//                                                 className="text-red-500 hover:text-red-600 text-lg"
//                                                 aria-label="Delete box"
//                                             >
//                                                 <MdDelete />
//                                             </button>
//                                         </div>

//                                     </div>
//                                 </div>
//                             ))}
//                             <button
//                                 onClick={addBox}
//                                 type="button"
//                                 className="mt-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
//                             >
//                                 + Add Box
//                             </button>
//                         </div>
//                     {/* </div> */}

//                     {/* Action Buttons */}
//                     <div className="flex flex-wrap justify-end gap-4 mt-8">
//                         <button
//                             type="button"
//                             onClick={handleClose}
//                             className="w-full sm:w-auto bg-gray-500 text-white py-3 px-6 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 font-medium transition-colors flex items-center justify-center"
//                         >
//                             <FaTimes className="mr-2" /> Cancel
//                         </button>
//                         {isEditing && (
//                             <button
//                                 type="submit"
//                                 disabled={isLoading}
//                                 className={`
//                                     w-full sm:w-auto 
//                                     bg-gradient-to-r from-purple-600 to-indigo-700 
//                                     text-white py-3 px-6 rounded-lg 
//                                     hover:from-purple-700 hover:to-indigo-800 
//                                     focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 
//                                     font-medium transition-colors flex items-center justify-center shadow-md
//                                     disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
//                                 `}
//                             >
//                                 {isLoading ? (
//                                     <>
//                                         <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                                         </svg>
//                                         Saving...
//                                     </>
//                                 ) : (
//                                     <>
//                                         <FaSave className="mr-2" /> Save Changes
//                                     </>
//                                 )}
//                             </button>
//                         )}
//                     </div>
//                 </form>
//             </DialogContent>
//         </Dialog>
//     );
// };

// export default B2BViewAllShipment;